import { Spacing } from '@persona-id/design-tokens';
import MarkdownToJsx from 'markdown-to-jsx';
import { ComponentProps, MouseEvent, useMemo } from 'react';
import styled from 'styled-components';

const MARKDOWN_OPTIONS: ComponentProps<typeof MarkdownToJsx>['options'] = {
  disableParsingRawHTML: true,
  overrides: {
    // Make all anchor tags open in new window
    a: {
      props: {
        target: '_blank',
        rel: 'noreferrer',
        onClick: (e: MouseEvent<HTMLAnchorElement>) => {
          // People often want to put links inside checkbox labels which causes
          // a11y issues. This at least makes it behave less poorly by making
          // link clicks not change checkbox state.
          e.stopPropagation();
        },
      },
    },
  },
};

interface MarkdownProps<WrapperType extends keyof JSX.IntrinsicElements> {
  id?: string;
  className?: string;
  children: string;
  markdownOptions?: ComponentProps<typeof MarkdownToJsx>['options'];
  wrapper?: WrapperType;
  /**
   * Props to be passed to the custom wrapper, if present
   */
  wrapperProps?: JSX.IntrinsicElements[WrapperType];
}

/**
 * This component is a thin wrapper around markdown-to-jsx, where we enforce
 * parsing raw HTML is disabled. The package markdown-to-jsx allows us convert
 * a string written in Markdown to JSX.
 *
 * We use `any` prop to allow passing props that the wrapper may expect.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const Markdown = <WrapperType extends keyof JSX.IntrinsicElements>({
  id,
  className,
  children,
  markdownOptions,
  wrapper,
  wrapperProps,
}: MarkdownProps<WrapperType>) => {
  const wrapperConfig =
    wrapper != null
      ? {
          wrapper,
          forceWrapper: true,
        }
      : {};

  const mergedMarkedownOptions = useMemo(
    () => ({
      ...MARKDOWN_OPTIONS,
      ...wrapperConfig,
      ...markdownOptions,
    }),
    [wrapper, markdownOptions],
  );

  return (
    <Container id={id} className={className}>
      <MarkdownToJsx {...wrapperProps} options={mergedMarkedownOptions}>
        {children ?? ''}
      </MarkdownToJsx>
    </Container>
  );
};

export default Markdown;

const Container = styled.div`
  /* To respect element margins and prevent margin collapse */
  display: flex;
  flex-direction: column;

  /* FIXME: remove once we clean up global styles that hardcode anchor font-size. */
  a {
    font-size: inherit;
  }

  p + p,
  ol + p,
  ul + p {
    /* Add spacing between custom translation paragraphs */
    margin-top: ${Spacing[5]};
  }

  p + ol,
  p + ul,
  ol + ol,
  ol + ul,
  ul + ol,
  ul + ul {
    margin-top: 0.75rem;
  }

  ol,
  ul {
    li + li,
    p + p {
      /* Space out items and paragraphs in items */
      margin-top: 0.75rem;
    }

    p {
      /**
       * Our reset overrides p to be display: block which causes alignment
       * with the :marker to break. This changes to the browser default which
       * is display: inline.
       **/
      display: initial;
    }
  }

  ol {
    /**
     * We don't use list-style-position: inside because we want subsequent
     * paragraphs to be left-aligned.
     **/
    margin-left: 1.8ch;
  }

  ul {
    list-style: none;

    /* Custom color and positioning for bullets */
    li::before {
      content: '•';
      font-size: 150%;
      /* System font stack to handle middot size inconsistencies across fonts */
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial,
        sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol';
      line-height: 0;
      margin: 0 0.5rem 0 0.25rem;
      position: relative;
      top: 1px;
      color: ${({ theme }) => theme.colorFont};
    }
  }
`;
